import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from "@/context/AuthContext";
import { AiOutlinePlus, AiOutlineLoading3Quarters } from 'react-icons/ai';
import { FiSettings, FiLayers, FiExternalLink } from 'react-icons/fi';
import { GoArrowLeft } from "react-icons/go";
import { motion } from "framer-motion";
import { 
  ComponentList, 
  ComponentDialog, 
  FormFields, 
  initialComponentState,
  formSchema,
  generateSlug
} from "@/components/Form";
import { z } from 'zod';

const FormCreate = () => {
  const navigate = useNavigate();
  const { axiosInstance, user } = useAuth();
  const { toast } = useToast();
  
  // Helper functions for form URL
  const getFormBaseUrl = () => {
    return import.meta.env.VITE_FORMS_FRONTEND_BASE_URL || 'https://forms.status.bot';
  };
  
  const getFormDisplayDomain = () => {
    const baseUrl = getFormBaseUrl();
    return baseUrl.replace(/^https?:\/\//, '');
  };
  
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    password: '',
    organization: '',
    components: [],
    online: true
  });
  
  const [loading, setLoading] = useState(false);
  const [showComponentDialog, setShowComponentDialog] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  
  // Slug validation states
  const [slugValidating, setSlugValidating] = useState(false);
  const [slugAvailable, setSlugAvailable] = useState(null);
  const [slugChecked, setSlugChecked] = useState(false);
  
  // Component errors state
  const [errors, setErrors] = useState({});
  
  // Component to add/edit
  const [componentToAdd, setComponentToAdd] = useState(initialComponentState);
  
  // Debounce function
  const debounce = (func, delay) => {
    let timeoutId;
    return (...args) => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      timeoutId = setTimeout(() => {
        func(...args);
      }, delay);
    };
  };
  
  // Check if slug is available
  const checkSlugAvailability = useCallback(
    debounce(async (slug) => {
      if (!slug) {
        setSlugChecked(false);
        setSlugAvailable(null);
        return;
      }
      
      setSlugValidating(true);
      setSlugChecked(false);
      
      try {
        const response = await axiosInstance.get(`/forms/check-slug/${slug}`);
        setSlugAvailable(response.data.available);
        setSlugChecked(true);
        
        // Update form errors if needed
        if (!response.data.available) {
          setFormErrors(prev => ({
            ...prev,
            slug: 'This form URL is already taken'
          }));
        } else {
          setFormErrors(prev => ({
            ...prev,
            slug: null
          }));
        }
      } catch (error) {
        console.error('Error checking slug:', error);
        setSlugAvailable(null);
      } finally {
        setSlugValidating(false);
      }
    }, 500),
    [axiosInstance]
  );
  
  // Validate slug when it changes
  useEffect(() => {
    if (formData.slug) {
      checkSlugAvailability(formData.slug);
    } else {
      setSlugChecked(false);
      setSlugAvailable(null);
    }
  }, [formData.slug, checkSlugAvailability]);
  
  const handleFormChange = (field, value) => {
    // Clear error when field changes
    setFormErrors({
      ...formErrors,
      [field]: null
    });
    
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Auto-generate slug when name changes
    if (field === 'name') {
      // Only auto-generate slug if it's empty or derived from name
      if (!formData.slug || formData.slug === generateSlug(formData.name)) {
        handleFormChange('slug', generateSlug(value));
      }
    }
    
    // Reset slug validation if needed
    if (field === 'slug') {
      setSlugChecked(false);
    }
  };
  
  const handleComponentChange = (field, value, resetForm = false) => {
    // Clear errors when user changes a field
    setErrors({
      ...errors,
      [field]: null
    });
    
    if (resetForm) {
      // Reset the form when changing type but keep the new type
      setComponentToAdd({
        ...initialComponentState,
        type: value
      });
    } else {
      // Convert numeric fields to numbers
      if (field === 'minLength' || field === 'maxLength') {
        value = value === '' ? '' : Number(value);
      }
      
      setComponentToAdd(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };
  
  const resetComponentForm = () => {
    setComponentToAdd(initialComponentState);
    setErrors({});
    setIsEditing(false);
    setEditingIndex(null);
  };
  
  const validateComponent = () => {
    const newErrors = {};
    
    // Common validation
    if (componentToAdd.type !== 'markdown' && !componentToAdd.name.trim()) {
      newErrors.name = 'Label is required';
    }
    
    // Type-specific validation
    if (componentToAdd.type === 'markdown' && !componentToAdd.content.trim()) {
      newErrors.content = 'Content is required';
    }
    
    if (componentToAdd.type === 'select' && 
        (!componentToAdd.options || componentToAdd.options.length === 0)) {
      newErrors.options = 'At least one option is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const addComponent = () => {
    if (!validateComponent()) {
      return false;
    }
    
    // Clean up fields based on component type
    const newComponent = { ...componentToAdd };
    
    // Only keep relevant fields for the component type
    if (newComponent.type !== 'markdown') {
      delete newComponent.content;
    } else {
      delete newComponent.name;
      delete newComponent.placeholder;
    }
    
    if (newComponent.type !== 'select') {
      delete newComponent.options;
    }
    
    // Remove placeholder for file uploads
    if (newComponent.type === 'file') {
      delete newComponent.placeholder;
      delete newComponent.rememberValue;
    } else {
      // Remove file-specific fields for non-file components
      delete newComponent.maxFiles;
    }
    
    if (isEditing && editingIndex !== null) {
      // Update existing component
      const updatedComponents = [...formData.components];
      updatedComponents[editingIndex] = newComponent;
      
      setFormData(prev => ({
        ...prev,
        components: updatedComponents
      }));
      
      toast({
        title: "Success",
        description: "Component updated successfully"
      });
    } else {
      // Add new component
      setFormData(prev => ({
        ...prev,
        components: [...prev.components, newComponent]
      }));
    }
    
    // Reset and close
    resetComponentForm();
    setShowComponentDialog(false);
    
    return true;
  };
  
  const removeComponent = (index) => {
    const updatedComponents = [...formData.components];
    updatedComponents.splice(index, 1);
    
    setFormData({
      ...formData,
      components: updatedComponents
    });
  };
  
  const handleDragEnd = (result) => {
    if (!result.destination) return;
    
    const items = Array.from(formData.components);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    
    setFormData({
      ...formData,
      components: items
    });
  };
  
  const validateForm = () => {
    console.log('Validating form...');
    try {
      formSchema.parse(formData);
      console.log('Schema validation passed');
      
      // Additional validation for slug availability
      if (!slugChecked || !slugAvailable) {
        console.log('Slug validation failed:', { slugChecked, slugAvailable });
        setFormErrors(prev => ({
          ...prev,
          slug: 'Please ensure the form URL is available'
        }));
        return false;
      }
      
      console.log('All validations passed');
      setFormErrors({});
      return true;
    } catch (error) {
      console.log('Schema validation error:', error);
      if (error instanceof z.ZodError) {
        const errors = {};
        error.errors.forEach((err) => {
          const path = err.path.join('.');
          errors[path] = err.message;
        });
        console.log('Validation errors:', errors);
        setFormErrors(errors);
        return false;
      }
      return false;
    }
  };
  
  const handleSubmit = async () => {
    console.log('Submit button clicked');
    console.log('Form data to submit:', formData);
    console.log('Form has components:', formData.components.length > 0);
    console.log('Loading state:', loading);
    console.log('Slug validation state:', { slugChecked, slugAvailable });
    
    // Check if button should be disabled (for debugging)
    const buttonShouldBeDisabled = loading || formData.components.length === 0 || (slugChecked && !slugAvailable);
    console.log('Button should be disabled:', buttonShouldBeDisabled);
    
    if (!validateForm()) {
      console.log('Form validation failed');
      toast({
        title: "Validation Error",
        description: "Please fix the errors in the form",
        variant: "destructive"
      });
      return;
    }
    
    console.log('Form validation successful, sending to server');
    setLoading(true);

    try {
      const response = await axiosInstance.post('/forms/new', formData);
      console.log('Server response:', response);
      toast({
        title: "Success",
        description: "Form created successfully"
      });
      navigate('/forms');
    } catch (error) {
      console.error('Error creating form:', error);
      toast({
        title: "Error",
        description: error.response?.data?.message || 'Error creating form',
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };
  
  const editComponent = (index) => {
    const component = formData.components[index];
    
    // Populate the form with component data
    setComponentToAdd({
      ...initialComponentState,
      ...component
    });
    
    setIsEditing(true);
    setEditingIndex(index);
    setShowComponentDialog(true);
  };
  
  // Loading state component
    const LoadingState = () => (
        <div className="fixed top-0 left-0 w-full h-full flex items-center justify-center space-x-4 animate-pulse">
            <AiOutlineLoading3Quarters className="text-2xl text-primary animate-spin" />
            <h1 className="text-xl font-semibold">Creating your form</h1>
        </div>
    );

  const fadeInVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: { opacity: 1, scale: 1 }
  };
  
  return (
    <Layout>
      <motion.div
        variants={fadeInVariants}
        initial="hidden"
        animate="visible"
        transition={{ duration: 0.5 }}
        className="space-y-6 pb-6"
      >
        {/* Header */}
        <div className="flex justify-between items-center">
          <div className="flex-col items-center space-y-2">
            <div className="flex items-center space-x-4">
              <div 
                className="flex items-center space-x-4 cursor-pointer text-3xl group"
                onClick={() => navigate('/forms')}
              >
                <GoArrowLeft className="group-hover:text-violet-500 transition-colors" />
                <h1 className="-mt-0.5 font-semibold">Create New Form</h1>
              </div>
            </div>
            <div className="flex items-center space-x-2 pl-12">
              <span className="text-sm text-muted-foreground -mt-0.5">
                Build a custom form for collecting feedback and reports
              </span>
              {formData.slug && (
                <>
                  <span className="text-muted-foreground">•</span>
                  <span className="text-sm text-muted-foreground">
                    {getFormDisplayDomain()}/{formData.slug}
                  </span>
                </>
              )}
            </div>
          </div>
          
          <div className="flex items-center space-x-4">
            <Button 
              onClick={handleSubmit} 
              disabled={loading || formData.components.length === 0 || (slugChecked && !slugAvailable)}
            >
              {loading ? (
                <div className="flex items-center">
                  <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin mr-2"></div>
                  Creating...
                </div>
              ) : (
                'Create Form'
              )}
            </Button>
          </div>
        </div>

        {/* Main Content */}
        <motion.div
          variants={fadeInVariants}
          transition={{ delay: 0.2 }}
        >
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Form Settings - Left Column */}
            <div className="lg:col-span-1">
              <div className="sticky top-6">
                <div className="bg-card rounded-xl border border-border/50 p-6 shadow-sm">
                  <div className="flex items-center space-x-2 mb-6">
                    <div className="p-2 bg-primary/10 rounded-lg">
                      <FiSettings className="h-4 w-4 text-primary" />
                    </div>
                    <div>
                      <h2 className="font-semibold">Form Settings</h2>
                      <p className="text-sm text-muted-foreground">Configure your form details</p>
                    </div>
                  </div>
                  
                  <FormFields 
                    formData={formData}
                    handleFormChange={handleFormChange}
                    formErrors={formErrors}
                    slugValidating={slugValidating}
                    slugChecked={slugChecked}
                    slugAvailable={slugAvailable}
                    preselect={true}
                  />
                </div>
              </div>
            </div>
            
            {/* Form Builder - Right Column */}
            <div className="lg:col-span-2">
              <div className="bg-card rounded-xl border border-border/50 shadow-sm">
                <div className="p-6 border-b border-border/50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <div className="p-2 bg-primary/10 rounded-lg">
                        <FiLayers className="h-4 w-4 text-primary" />
                      </div>
                      <div>
                        <h2 className="font-semibold">Form Components</h2>
                        <p className="text-sm text-muted-foreground">
                          {formData.components.length} component{formData.components.length !== 1 ? 's' : ''} added
                        </p>
                      </div>
                    </div>
                    
                    <Button 
                      onClick={() => {
                        resetComponentForm();
                        setShowComponentDialog(true);
                      }}
                      size="sm"
                      className="flex items-center"
                      variant="secondary"
                    >
                      <AiOutlinePlus className="h-4 w-4 mr-2" />
                      Add Component
                    </Button>
                  </div>
                </div>
                
                <div className="p-4">
                  <ComponentList 
                    components={formData.components}
                    onDragEnd={handleDragEnd}
                    onEditComponent={editComponent}
                    onRemoveComponent={removeComponent}
                    formErrors={formErrors}
                  />
                </div>
              </div>
            </div>
          </div>
        </motion.div>
        
        <ComponentDialog 
          open={showComponentDialog}
          onOpenChange={(open) => {
            if (!open) resetComponentForm();
            setShowComponentDialog(open);
          }}
          componentData={componentToAdd}
          onComponentChange={handleComponentChange}
          onAddComponent={addComponent}
          isEditing={isEditing}
          errors={errors}
        />
      </motion.div>
      
      {loading && <LoadingState />}
    </Layout>
  );
};

export default FormCreate; 